import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUpload } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { Tabs, Select } from 'antd';
import ApplicationService from '../services/axios.service';

const { TabPane } = Tabs;
const service = new ApplicationService();

const SalesTotal = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  
  const [data, setData] = useState({});
  const [budgetData, setBudgetData] = useState({});
  const [loading, setLoading] = useState(false);
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedQuarters, setSelectedQuarters] = useState(['Q1']);
  const [budgetYear, setBudgetYear] = useState(currentYear);
  const [budgetQuarters, setBudgetQuarters] = useState(['Q1']);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  const [budgetEditingCell, setBudgetEditingCell] = useState(null);
  const [budgetEditValue, setBudgetEditValue] = useState('');
  const [budgetHasUnsavedChanges, setBudgetHasUnsavedChanges] = useState(false);
  const [budgetPendingChanges, setBudgetPendingChanges] = useState({});
  const editValueRef = useRef('');
  const editingCellRef = useRef(null);

  // Update refs when state changes
  useEffect(() => {
    editValueRef.current = editValue;
  }, [editValue]);

  useEffect(() => {
    editingCellRef.current = editingCell;
  }, [editingCell]);

  // ✅ Fetch on component mount
  useEffect(() => {
    fetchSalesTotalData();
    fetchBudgetData();
  }, []);

  const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];

  // Get current month and year
  const getCurrentMonthYear = () => {
    const now = new Date();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[now.getMonth()]} ${now.getFullYear()}`;
  };

  const isCurrentMonth = (monthKey) => {
    const currentMonthYear = getCurrentMonthYear();
    return monthKey === currentMonthYear;
  };

  const fetchSalesTotalData = async () => {
    if (selectedQuarters.length === 0) {
      toast.warning('Please select at least one quarter');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        year: selectedYear,
        quarters: selectedQuarters.join(',')
      };

      const response = await service.getSalesTotalGrouped(payload);
      console.log('Sales Total Data:', response);
      
      if (response.data.success) {
        setData(response.data.data || {});
        toast.success('Data fetched successfully!');
      } else {
        setData({});
        toast.info('No data found');
      }
    } catch (error) {
      console.error('Error fetching sales total:', error);
      toast.error('Failed to fetch sales total data');
      setData({});
    } finally {
      setLoading(false);
    }
  };

  const handleQuarterChange = (quarter) => {
    setSelectedQuarters(prev => 
      prev.includes(quarter) 
        ? prev.filter(q => q !== quarter)
        : [...prev, quarter]
    );
  };

  const handleCellEdit = (customerName, partNumber, month, field, value) => {
    setData(prevData => {
      const newData = { ...prevData };
      const monthRecords = newData[month];
      const recordIndex = monthRecords.findIndex(
        r => r.customer_name === customerName && r.part_number === partNumber
      );
      if (recordIndex > -1) {
        monthRecords[recordIndex] = {
          ...monthRecords[recordIndex],
          [field]: value
        };
      }
      return newData;
    });
  };

  const startEdit = (customerName, partNumber, month, field, currentValue) => {
    const cellData = { customerName, partNumber, month, field };
    
    // Handle both string and number values from API
    let valueToEdit = '';
    if (currentValue !== null && currentValue !== undefined && currentValue !== '') {
      // Convert to number first, then to string for editing
      const numVal = typeof currentValue === 'string' ? parseFloat(currentValue) : currentValue;
      valueToEdit = isNaN(numVal) ? '' : String(numVal);
    }
    
    setEditingCell(cellData);
    setEditValue(valueToEdit);
    editingCellRef.current = cellData;
    editValueRef.current = valueToEdit;
  };

  const saveEdit = () => {
    const cell = editingCellRef.current;
    const value = editValueRef.current;
    
    if (!cell) return;

    // Convert to number for saving
    const numericValue = value === '' || value === null ? 0 : parseFloat(value);
    
    if (isNaN(numericValue)) {
      toast.error('Invalid number');
      cancelEdit();
      return;
    }

    const key = `${cell.customerName}_${cell.partNumber}_${cell.month}_${cell.field}`;
    
    setPendingChanges(prev => ({
      ...prev,
      [key]: {
        customerName: cell.customerName,
        partNumber: cell.partNumber,
        month: cell.month,
        field: cell.field,
        value: numericValue
      }
    }));

    setData(prevData => {
      const newData = JSON.parse(JSON.stringify(prevData));
      const monthRecords = newData[cell.month];
      
      if (monthRecords) {
        const recordIndex = monthRecords.findIndex(
          r => r.customer_name === cell.customerName && r.part_number === cell.partNumber
        );
        
        if (recordIndex > -1) {
          // Store as number to match API format
          monthRecords[recordIndex][cell.field] = numericValue;
        }
      }
      
      return newData;
    });

    setHasUnsavedChanges(true);
    cancelEdit();
  };

  const handleSaveAll = async () => {
    if (Object.keys(pendingChanges).length === 0) {
      toast.info("No changes to save");
      return;
    }

    try {
      // Group changes by record
      const recordUpdates = {};
      
      Object.values(pendingChanges).forEach(change => {
        const recordKey = `${change.customerName}_${change.partNumber}_${change.month}`;
        if (!recordUpdates[recordKey]) {
          recordUpdates[recordKey] = {
            customerName: change.customerName,
            partNumber: change.partNumber,
            month: change.month,
            fields: {}
          };
        }
        recordUpdates[recordKey].fields[change.field] = change.value;
      });

      // Save each record
      for (const update of Object.values(recordUpdates)) {
        const monthRecords = data[update.month];
        const record = monthRecords.find(
          r => r.customer_name === update.customerName && r.part_number === update.partNumber
        );

        if (record) {
          const payload = {
            id: record.id,
            customer_name: update.customerName,
            part_number: update.partNumber,
            month: update.month,
            ...update.fields
          };

          await service.updateSalesTotal(payload);
        }
      }

      toast.success("All changes saved successfully!");
      setHasUnsavedChanges(false);
      setPendingChanges({});
    } catch (error) {
      console.error("Error saving changes:", error);
      toast.error("Error saving changes");
    }
  };

  const handleCancelAll = () => {
    setPendingChanges({});
    setHasUnsavedChanges(false);
    fetchSalesTotalData(); // Reload original data
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
    editingCellRef.current = null;
    editValueRef.current = '';
  };

  const isEditing = (customerName, partNumber, month, field) => {
    return editingCell?.customerName === customerName &&
           editingCell?.partNumber === partNumber &&
           editingCell?.month === month &&
           editingCell?.field === field;
  };

const renderDataTable = (forecastData) => {
  console.log('Rendering table with data:', forecastData);

  if (
    !forecastData ||
    !forecastData.data ||
    !Array.isArray(forecastData.data) ||
    forecastData.data.length === 0
  ) {
    return <div className="text-center py-8 text-gray-500">No data available</div>;
  }

  const data = forecastData.data; // now this is an array like [{ label, values }]
  const groupedRecords = {};

  data.forEach((item) => {
    const period = item.label;
    const records = item.values || [];

    if (Array.isArray(records)) {
      records.forEach((record) => {
        const key = `${record.customer_name}_${record.part_number}`;

        if (!groupedRecords[key]) {
          groupedRecords[key] = {
            customer_name: record.customer_name,
            part_number: record.part_number,
            model_name: record.model_name,
            months: {}
          };
        }

        groupedRecords[key].months[period] = record;
      });
    }
  });

  // preserve API order directly
  const periods = data.map((item) => item.label);

  const currentYear = new Date().getFullYear();

  const isFutureYear = (period) => {
    if (/^\d{4}$/.test(period)) {
      return parseInt(period, 10) > currentYear;
    }

    const yearMatch = period.match(/\d{4}$/);
    if (yearMatch) {
      return parseInt(yearMatch[0], 10) > currentYear;
    }

    return false;
  };

  return (
    <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
      <table
        className="min-w-full border-collapse border border-gray-300"
        style={{ fontSize: '12px' }}
      >
        <thead className="sticky top-0 z-20">
          <tr className="bg-yellow-300" style={{ height: '41px' }}>
            <th
              className="border border-gray-300 px-2 py-1 text-center sticky left-0 bg-yellow-300 z-30"
              rowSpan="2"
              style={{ minWidth: '150px' }}
            >
              Customer
            </th>
            <th
              className="border border-gray-300 px-2 py-1 text-center sticky left-[150px] bg-yellow-300 z-30"
              rowSpan="2"
              style={{ minWidth: '120px' }}
            >
              Part Number
            </th>
            <th
              className="border border-gray-300 px-2 py-1 text-center sticky left-[270px] bg-yellow-300 z-30"
              rowSpan="2"
              style={{ minWidth: '150px' }}
            >
              Model Name
            </th>

            {periods.map((period, idx) => {
              const isFuture = isFutureYear(period);
              const colSpan = isFuture ? 14 : 3;

              return (
                <th
                  key={idx}
                  className="border border-gray-300 px-2 py-1 text-center bg-yellow-300"
                  colSpan={colSpan}
                >
                  {period}
                </th>
              );
            })}
          </tr>

          <tr
            className="bg-blue-100"
            style={{ position: 'sticky', top: '41px', zIndex: 20, height: '41px' }}
          >
            {periods.map((period, idx) => {
              const isFuture = isFutureYear(period);

              return (
                <React.Fragment key={idx}>
                  <th className="border border-gray-300 px-1 py-1 bg-blue-100" style={{ fontSize: '11px', minWidth: '80px' }}>
                    Unit Price
                  </th>
                  <th className="border border-gray-300 px-1 py-1 bg-blue-100" style={{ fontSize: '11px', minWidth: '80px' }}>
                    Qty
                  </th>
                  <th className="border border-gray-300 px-1 py-1 bg-blue-100" style={{ fontSize: '11px', minWidth: '80px' }}>
                    Amt
                  </th>

                  {isFuture && (
                    <>
                      <th className="border border-gray-300 px-1 py-1 bg-red-100" style={{ fontSize: '11px', minWidth: '80px' }}>
                        Firm PO Qty
                      </th>
                      <th className="border border-gray-300 px-1 py-1 bg-red-100" style={{ fontSize: '11px', minWidth: '80px' }}>
                        Firm PO Amt
                      </th>
                      <th className="border border-gray-300 px-1 py-1 bg-orange-100" style={{ fontSize: '11px', minWidth: '80px' }}>
                        Firm PO Risk Qty
                      </th>
                      <th className="border border-gray-300 px-1 py-1 bg-orange-100" style={{ fontSize: '11px', minWidth: '80px' }}>
                        Firm PO Risk Amt
                      </th>
                      <th className="border border-gray-300 px-1 py-1 bg-green-100" style={{ fontSize: '11px', minWidth: '80px' }}>
                        Low Risk Qty
                      </th>
                      <th className="border border-gray-300 px-1 py-1 bg-green-100" style={{ fontSize: '11px', minWidth: '80px' }}>
                        Low Risk Amt
                      </th>
                      <th className="border border-gray-300 px-1 py-1 bg-yellow-100" style={{ fontSize: '11px', minWidth: '80px' }}>
                        High Risk Qty
                      </th>
                      <th className="border border-gray-300 px-1 py-1 bg-yellow-100" style={{ fontSize: '11px', minWidth: '80px' }}>
                        High Risk Amt
                      </th>
                      <th className="border border-gray-300 px-1 py-1 bg-purple-100" style={{ fontSize: '11px', minWidth: '80px' }}>
                        Markup Qty
                      </th>
                      <th className="border border-gray-300 px-1 py-1 bg-purple-100" style={{ fontSize: '11px', minWidth: '80px' }}>
                        Markup Amt
                      </th>
                      <th className="border border-gray-300 px-1 py-1 bg-pink-100" style={{ fontSize: '11px', minWidth: '80px' }}>
                        Delta
                      </th>
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {Object.values(groupedRecords).map((group, rowIdx) => (
            <tr key={rowIdx} className="hover:bg-gray-50">
              <td
                className="border border-gray-300 px-2 py-1 sticky left-0 bg-white z-10"
                style={{ minWidth: '150px', fontSize: '13px' }}
              >
                {group.customer_name}
              </td>
              <td
                className="border border-gray-300 px-2 py-1 sticky left-[150px] bg-white z-10"
                style={{ minWidth: '120px', fontSize: '13px' }}
              >
                {group.part_number}
              </td>
              <td
                className="border border-gray-300 px-2 py-1 sticky left-[270px] bg-white z-10"
                style={{ minWidth: '150px', fontSize: '13px' }}
              >
                {group.model_name}
              </td>

              {periods.map((period, periodIdx) => {
                const record = group.months[period] || {};
                const isFuture = isFutureYear(period);

                return (
                  <React.Fragment key={periodIdx}>
                    <td className="border border-gray-300 px-2 py-1 text-right bg-gray-50" style={{ fontSize: '13px', minWidth: '80px' }}>
                      {record.unit_price || 0}
                    </td>
                    <td className="border border-gray-300 px-2 py-1 text-right bg-gray-50" style={{ fontSize: '13px', minWidth: '80px' }}>
                      {record.qty || 0}
                    </td>
                    <td className="border border-gray-300 px-2 py-1 text-right bg-gray-50" style={{ fontSize: '13px', minWidth: '80px' }}>
                      {record.amt || 0}
                    </td>

                    {isFuture && (
                      <>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-red-50" style={{ fontSize: '13px', minWidth: '80px' }}>
                          {record.firm_po_qty || 0}
                        </td>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-red-50" style={{ fontSize: '13px', minWidth: '80px' }}>
                          {record.firm_po_amt || 0}
                        </td>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-orange-50" style={{ fontSize: '13px', minWidth: '80px' }}>
                          {record.firm_po_risk_qty || 0}
                        </td>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-orange-50" style={{ fontSize: '13px', minWidth: '80px' }}>
                          {record.firm_po_risk_amt || 0}
                        </td>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-green-50" style={{ fontSize: '13px', minWidth: '80px' }}>
                          {record.low_risk_qty || 0}
                        </td>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-green-50" style={{ fontSize: '13px', minWidth: '80px' }}>
                          {record.low_risk_amt || 0}
                        </td>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-yellow-50" style={{ fontSize: '13px', minWidth: '80px' }}>
                          {record.high_risk_qty || 0}
                        </td>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-yellow-50" style={{ fontSize: '13px', minWidth: '80px' }}>
                          {record.high_risk_amt || 0}
                        </td>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-purple-50" style={{ fontSize: '13px', minWidth: '80px' }}>
                          {record.markup_qty || 0}
                        </td>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-purple-50" style={{ fontSize: '13px', minWidth: '80px' }}>
                          {record.markup_amt || 0}
                        </td>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-pink-50" style={{ fontSize: '13px', minWidth: '80px' }}>
                          {record.delta || 0}
                        </td>
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

  const fetchBudgetData = async () => {
    if (budgetQuarters.length === 0) {
      toast.warning('Please select at least one quarter');
      return;
    }

    setBudgetLoading(true);
    try {
      const payload = {
        year: budgetYear,
        quarters: budgetQuarters.join(',')
      };

      const response = await service.getSalesBudgetGrouped(payload);
      console.log('Budget Data:', response);
      
      if (response.data.success) {
        setBudgetData(response.data.data || {});
        toast.success('Budget data fetched successfully!');
      } else {
        setBudgetData({});
        toast.info('No budget data found');
      }
    } catch (error) {
      console.error('Error fetching budget data:', error);
      toast.error('Failed to fetch budget data');
      setBudgetData({});
    } finally {
      setBudgetLoading(false);
    }
  };

  const renderBudgetTableOld = (budgetData) => {
  if (budgetLoading) {
    return (
      <div className="flex justify-center items-center py-10">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  if (
    !budgetData ||
    !budgetData.data ||
    !Array.isArray(budgetData.data) ||
    budgetData.data.length === 0
  ) {
    return (
      <div className="text-center py-10 text-gray-500">
        No data available. Please select filters and click Search.
      </div>
    );
  }

  const data = budgetData.data; // array from API
  const groupedRecords = {};

  data.forEach((item) => {
    const month = item.label;
    const records = item.values || [];

    if (Array.isArray(records)) {
      records.forEach((record) => {
        const key = `${record.customer_name}_${record.part_number}`;
        if (!groupedRecords[key]) {
          groupedRecords[key] = {
            customer_name: record.customer_name,
            part_number: record.part_number,
            model_name: record.model_name,
            months: {}
          };
        }
        groupedRecords[key].months[month] = record;
      });
    }
  });

  const periods = data.map((item) => item.label);

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonthNum = currentDate.getMonth() + 1;

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const currentMonthName = monthNames[currentMonthNum - 1];
  const currentMonth = `${currentMonthName} ${currentYear}`;

  const isCurrentMonth = (month) => {
    return month === currentMonth;
  };

  const isPastMonth = (month) => {
    if (/^\d{4}$/.test(month)) {
      return false;
    }

    const [monthName, yearStr] = month.split(' ');
    const monthYear = new Date(parseInt(yearStr, 10), monthNames.indexOf(monthName));
    const current = new Date(currentYear, currentMonthNum - 1);
    return monthYear < current;
  };

  const isFutureMonth = (month) => {
    if (/^\d{4}$/.test(month)) {
      return parseInt(month, 10) > currentYear;
    }

    const [monthName, yearStr] = month.split(' ');
    const monthYear = new Date(parseInt(yearStr, 10), monthNames.indexOf(monthName));
    const current = new Date(currentYear, currentMonthNum - 1);
    return monthYear > current;
  };

  const renderEditableCell = (customerName, partNumber, month, field, value, bgColor = '') => {
    const cellKey = `${customerName}_${partNumber}_${month}_${field}`;
    const isCurrentlyEditing = editingCell === cellKey;

    const handleSave = () => {
      const numericValue = parseFloat(editValue);

      if (isNaN(numericValue)) {
        setEditingCell(null);
        setEditValue('');
        return;
      }

      setBudgetPendingChanges((prev) => ({
        ...prev,
        [cellKey]: {
          customerName,
          partNumber,
          month,
          field,
          value: numericValue
        }
      }));

      setBudgetData((prevData) => {
        if (!prevData || !prevData.data || !Array.isArray(prevData.data)) {
          return prevData;
        }

        const newData = {
          ...prevData,
          data: prevData.data.map((item) => {
            if (item.label !== month) return item;

            return {
              ...item,
              values: item.values.map((record) => {
                if (
                  record.customer_name === customerName &&
                  record.part_number === partNumber
                ) {
                  return {
                    ...record,
                    [field]: numericValue
                  };
                }
                return record;
              })
            };
          })
        };

        return newData;
      });

      setBudgetHasUnsavedChanges(true);
      setEditingCell(null);
      setEditValue('');
    };

    const handleCancel = () => {
      setEditingCell(null);
      setEditValue('');
    };

    return (
      <td
        className={`border border-gray-300 px-2 py-1 text-right ${bgColor}`}
        style={{ fontSize: '11px', minWidth: '80px' }}
      >
        {isCurrentlyEditing ? (
          <input
            type="number"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') handleCancel();
            }}
            autoFocus
            className="w-full px-1 border border-blue-500 focus:outline-none"
            style={{ fontSize: '11px' }}
          />
        ) : (
          <span
            onDoubleClick={() => {
              setEditingCell(cellKey);
              setEditValue(value || 0);
            }}
            className="cursor-pointer hover:bg-blue-50 block w-full h-full px-1"
            title="Double-click to edit"
          >
            {value || 0}
          </span>
        )}
      </td>
    );
  };

  return (
    <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
      <table className="min-w-full border-collapse border border-gray-300" style={{ fontSize: '13px' }}>
        <thead className="sticky top-0 z-20">
          <tr className="bg-gray-100">
            <th className="border border-gray-300 px-2 py-2 sticky left-0 bg-gray-100 z-30" style={{ minWidth: '150px', fontSize: '13px' }}>
              Customer Name
            </th>
            <th className="border border-gray-300 px-2 py-2 sticky left-[150px] bg-gray-100 z-30" style={{ minWidth: '120px', fontSize: '13px' }}>
              Part Number
            </th>
            <th className="border border-gray-300 px-2 py-2 sticky left-[270px] bg-gray-100 z-30" style={{ minWidth: '150px', fontSize: '13px' }}>
              Model Name
            </th>

            {periods.map((month, idx) => {
              const isPast = isPastMonth(month);
              const colSpan = isPast ? 3 : 9;

              return (
                <th key={idx} className="border border-gray-300 px-2 py-2 bg-blue-50" colSpan={colSpan}>
                  <div className="text-center font-semibold" style={{ fontSize: '13px' }}>
                    {month}
                  </div>
                </th>
              );
            })}
          </tr>

          <tr className="bg-gray-50">
            <th className="border border-gray-300 sticky left-0 bg-gray-50 z-30"></th>
            <th className="border border-gray-300 sticky left-[150px] bg-gray-50 z-30"></th>
            <th className="border border-gray-300 sticky left-[270px] bg-gray-50 z-30"></th>

            {periods.map((month, idx) => {
              const isPast = isPastMonth(month);

              return (
                <React.Fragment key={idx}>
                  <th className="border border-gray-300 px-1 py-1" style={{ fontSize: '13px', minWidth: '80px' }}>Unit Price</th>
                  <th className="border border-gray-300 px-1 py-1" style={{ fontSize: '13px', minWidth: '80px' }}>Qty</th>
                  <th className="border border-gray-300 px-1 py-1" style={{ fontSize: '13px', minWidth: '80px' }}>Amount</th>
                  {!isPast && (
                    <>
                      <th className="border border-gray-300 px-1 py-1 bg-blue-100" style={{ fontSize: '13px', minWidth: '80px' }}>CF Qty</th>
                      <th className="border border-gray-300 px-1 py-1 bg-blue-100" style={{ fontSize: '13px', minWidth: '80px' }}>CF Amt</th>
                      <th className="border border-gray-300 px-1 py-1 bg-green-100" style={{ fontSize: '13px', minWidth: '80px' }}>Pot Qty</th>
                      <th className="border border-gray-300 px-1 py-1 bg-green-100" style={{ fontSize: '13px', minWidth: '80px' }}>Pot Amt</th>
                      <th className="border border-gray-300 px-1 py-1 bg-yellow-100" style={{ fontSize: '13px', minWidth: '80px' }}>Markup Qty</th>
                      <th className="border border-gray-300 px-1 py-1 bg-yellow-100" style={{ fontSize: '13px', minWidth: '80px' }}>Markup Amt</th>
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {Object.values(groupedRecords).map((group, rowIdx) => (
            <tr key={rowIdx} className="hover:bg-gray-50">
              <td className="border border-gray-300 px-2 py-1 sticky left-0 bg-white z-10" style={{ minWidth: '150px', fontSize: '13px' }}>
                {group.customer_name}
              </td>
              <td className="border border-gray-300 px-2 py-1 sticky left-[150px] bg-white z-10" style={{ minWidth: '120px', fontSize: '13px' }}>
                {group.part_number}
              </td>
              <td className="border border-gray-300 px-2 py-1 sticky left-[270px] bg-white z-10" style={{ minWidth: '150px', fontSize: '13px' }}>
                {group.model_name}
              </td>

              {periods.map((month, monthIdx) => {
                const record = group.months[month] || {};
                const isPast = isPastMonth(month);

                return (
                  <React.Fragment key={monthIdx}>
                    {isPast ? (
                      <>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-gray-50" style={{ fontSize: '13px', minWidth: '80px' }}>
                          {record.unit_price || 0}
                        </td>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-gray-50" style={{ fontSize: '13px', minWidth: '80px' }}>
                          {record.qty || 0}
                        </td>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-gray-50" style={{ fontSize: '13px', minWidth: '80px' }}>
                          {record.amt || 0}
                        </td>
                      </>
                    ) : (
                      <>
                        {renderEditableCell(group.customer_name, group.part_number, month, 'unit_price', record.unit_price)}
                        {renderEditableCell(group.customer_name, group.part_number, month, 'qty', record.qty)}
                        {renderEditableCell(group.customer_name, group.part_number, month, 'amt', record.amt)}
                        {renderEditableCell(group.customer_name, group.part_number, month, 'current_forecast_qty', record.current_forecast_qty, 'bg-blue-50')}
                        {renderEditableCell(group.customer_name, group.part_number, month, 'current_forecast_amt', record.current_forecast_amt, 'bg-blue-50')}
                        {renderEditableCell(group.customer_name, group.part_number, month, 'potential_qty', record.potential_qty, 'bg-green-50')}
                        {renderEditableCell(group.customer_name, group.part_number, month, 'potential_amt', record.potential_amt, 'bg-green-50')}
                        {renderEditableCell(group.customer_name, group.part_number, month, 'markup_qty', record.markup_qty, 'bg-yellow-50')}
                        {renderEditableCell(group.customer_name, group.part_number, month, 'markup_amt', record.markup_amt, 'bg-yellow-50')}
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
const renderBudgetTable = (budgetData) => {
  if (budgetLoading) {
    return (
      <div className="flex justify-center items-center py-10">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  if (
    !budgetData ||
    !budgetData.data ||
    !Array.isArray(budgetData.data) ||
    budgetData.data.length === 0
  ) {
    return (
      <div className="text-center py-10 text-gray-500">
        No data available. Please select filters and click Search.
      </div>
    );
  }

  const data = budgetData.data;
  const groupedRecords = {};

  data.forEach((item) => {
    const month = item.label;
    const records = item.values || [];

    if (Array.isArray(records)) {
      records.forEach((record) => {
        const key = `${record.customer_name}_${record.part_number}`;

        if (!groupedRecords[key]) {
          groupedRecords[key] = {
            customer_name: record.customer_name,
            part_number: record.part_number,
            model_name: record.model_name,
            months: {}
          };
        }

        groupedRecords[key].months[month] = record;
      });
    }
  });

  const periods = data.map((item) => item.label);

  const isYearOnly = (month) => /^\d{4}$/.test(month);

  const renderEditableCell = (customerName, partNumber, month, field, value, bgColor = '') => {
    const cellKey = `${customerName}_${partNumber}_${month}_${field}`;
    const isCurrentlyEditing = editingCell === cellKey;

    const handleSave = () => {
      const numericValue = parseFloat(editValue);

      if (isNaN(numericValue)) {
        setEditingCell(null);
        setEditValue('');
        return;
      }

      setBudgetPendingChanges((prev) => ({
        ...prev,
        [cellKey]: {
          customerName,
          partNumber,
          month,
          field,
          value: numericValue
        }
      }));

      setBudgetData((prevData) => {
        if (!prevData || !prevData.data || !Array.isArray(prevData.data)) {
          return prevData;
        }

        return {
          ...prevData,
          data: prevData.data.map((item) => {
            if (item.label !== month) {
              return item;
            }

            return {
              ...item,
              values: item.values.map((record) => {
                if (
                  record.customer_name === customerName &&
                  record.part_number === partNumber
                ) {
                  return {
                    ...record,
                    [field]: numericValue
                  };
                }
                return record;
              })
            };
          })
        };
      });

      setBudgetHasUnsavedChanges(true);
      setEditingCell(null);
      setEditValue('');
    };

    const handleCancel = () => {
      setEditingCell(null);
      setEditValue('');
    };

    return (
      <td
        className={`border border-gray-300 px-2 py-1 text-right ${bgColor}`}
        style={{ fontSize: '11px', minWidth: '80px' }}
      >
        {isCurrentlyEditing ? (
          <input
            type="number"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSave();
              }
              if (e.key === 'Escape') {
                handleCancel();
              }
            }}
            autoFocus
            className="w-full px-1 border border-blue-500 focus:outline-none"
            style={{ fontSize: '11px' }}
          />
        ) : (
          <span
            onDoubleClick={() => {
              setEditingCell(cellKey);
              setEditValue(value || 0);
            }}
            className="cursor-pointer hover:bg-blue-50 block w-full h-full px-1"
            title="Double-click to edit"
          >
            {value || 0}
          </span>
        )}
      </td>
    );
  };

  return (
    <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
      <table
        className="min-w-full border-collapse border border-gray-300"
        style={{ fontSize: '13px' }}
      >
        <thead className="sticky top-0 z-20">
          <tr className="bg-gray-100">
            <th
              className="border border-gray-300 px-2 py-2 sticky left-0 bg-gray-100 z-30"
              style={{ minWidth: '150px', fontSize: '13px' }}
            >
              Customer Name
            </th>
            <th
              className="border border-gray-300 px-2 py-2 sticky left-[150px] bg-gray-100 z-30"
              style={{ minWidth: '120px', fontSize: '13px' }}
            >
              Part Number
            </th>
            <th
              className="border border-gray-300 px-2 py-2 sticky left-[270px] bg-gray-100 z-30"
              style={{ minWidth: '150px', fontSize: '13px' }}
            >
              Model Name
            </th>

            {periods.map((month, idx) => {
              const showAllColumns = isYearOnly(month);
              const colSpan = showAllColumns ? 9 : 3;

              return (
                <th
                  key={idx}
                  className="border border-gray-300 px-2 py-2 bg-blue-50"
                  colSpan={colSpan}
                >
                  <div className="text-center font-semibold" style={{ fontSize: '13px' }}>
                    {month}
                  </div>
                </th>
              );
            })}
          </tr>

          <tr className="bg-gray-50">
            <th className="border border-gray-300 sticky left-0 bg-gray-50 z-30"></th>
            <th className="border border-gray-300 sticky left-[150px] bg-gray-50 z-30"></th>
            <th className="border border-gray-300 sticky left-[270px] bg-gray-50 z-30"></th>

            {periods.map((month, idx) => {
              const showAllColumns = isYearOnly(month);

              return (
                <React.Fragment key={idx}>
                  <th className="border border-gray-300 px-1 py-1" style={{ fontSize: '13px', minWidth: '80px' }}>
                    Unit Price
                  </th>
                  <th className="border border-gray-300 px-1 py-1" style={{ fontSize: '13px', minWidth: '80px' }}>
                    Qty
                  </th>
                  <th className="border border-gray-300 px-1 py-1" style={{ fontSize: '13px', minWidth: '80px' }}>
                    Amount
                  </th>

                  {showAllColumns && (
                    <>
                      <th className="border border-gray-300 px-1 py-1 bg-blue-100" style={{ fontSize: '13px', minWidth: '80px' }}>
                        CF Qty
                      </th>
                      <th className="border border-gray-300 px-1 py-1 bg-blue-100" style={{ fontSize: '13px', minWidth: '80px' }}>
                        CF Amt
                      </th>
                      <th className="border border-gray-300 px-1 py-1 bg-green-100" style={{ fontSize: '13px', minWidth: '80px' }}>
                        Pot Qty
                      </th>
                      <th className="border border-gray-300 px-1 py-1 bg-green-100" style={{ fontSize: '13px', minWidth: '80px' }}>
                        Pot Amt
                      </th>
                      <th className="border border-gray-300 px-1 py-1 bg-yellow-100" style={{ fontSize: '13px', minWidth: '80px' }}>
                        Markup Qty
                      </th>
                      <th className="border border-gray-300 px-1 py-1 bg-yellow-100" style={{ fontSize: '13px', minWidth: '80px' }}>
                        Markup Amt
                      </th>
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {Object.values(groupedRecords).map((group, rowIdx) => (
            <tr key={rowIdx} className="hover:bg-gray-50">
              <td
                className="border border-gray-300 px-2 py-1 sticky left-0 bg-white z-10"
                style={{ minWidth: '150px', fontSize: '13px' }}
              >
                {group.customer_name}
              </td>
              <td
                className="border border-gray-300 px-2 py-1 sticky left-[150px] bg-white z-10"
                style={{ minWidth: '120px', fontSize: '13px' }}
              >
                {group.part_number}
              </td>
              <td
                className="border border-gray-300 px-2 py-1 sticky left-[270px] bg-white z-10"
                style={{ minWidth: '150px', fontSize: '13px' }}
              >
                {group.model_name}
              </td>

              {periods.map((month, monthIdx) => {
                const record = group.months[month] || {};
                const showAllColumns = isYearOnly(month);

                return (
                  <React.Fragment key={monthIdx}>
                    {!showAllColumns ? (
                      <>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-gray-50" style={{ fontSize: '13px', minWidth: '80px' }}>
                          {record.unit_price || 0}
                        </td>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-gray-50" style={{ fontSize: '13px', minWidth: '80px' }}>
                          {record.qty || 0}
                        </td>
                        <td className="border border-gray-300 px-2 py-1 text-right bg-gray-50" style={{ fontSize: '13px', minWidth: '80px' }}>
                          {record.amt || 0}
                        </td>
                      </>
                    ) : (
                      <>
                        {renderEditableCell(group.customer_name, group.part_number, month, 'unit_price', record.unit_price)}
                        {renderEditableCell(group.customer_name, group.part_number, month, 'qty', record.qty)}
                        {renderEditableCell(group.customer_name, group.part_number, month, 'amt', record.amt)}
                        {renderEditableCell(group.customer_name, group.part_number, month, 'current_forecast_qty', record.current_forecast_qty, 'bg-blue-50')}
                        {renderEditableCell(group.customer_name, group.part_number, month, 'current_forecast_amt', record.current_forecast_amt, 'bg-blue-50')}
                        {renderEditableCell(group.customer_name, group.part_number, month, 'potential_qty', record.potential_qty, 'bg-green-50')}
                        {renderEditableCell(group.customer_name, group.part_number, month, 'potential_amt', record.potential_amt, 'bg-green-50')}
                        {renderEditableCell(group.customer_name, group.part_number, month, 'markup_qty', record.markup_qty, 'bg-yellow-50')}
                        {renderEditableCell(group.customer_name, group.part_number, month, 'markup_amt', record.markup_amt, 'bg-yellow-50')}
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
  const handleBudgetSaveAll = async () => {
    if (Object.keys(budgetPendingChanges).length === 0) {
      toast.info("No changes to save");
      return;
    }

    try {
      // Group changes by record
      const recordUpdates = {};
      
      Object.values(budgetPendingChanges).forEach(change => {
        const recordKey = `${change.customerName}_${change.partNumber}_${change.month}`;
        if (!recordUpdates[recordKey]) {
          recordUpdates[recordKey] = {
            customerName: change.customerName,
            partNumber: change.partNumber,
            month: change.month,
            fields: {}
          };
        }
        recordUpdates[recordKey].fields[change.field] = change.value;
      });

      // Save each record using budget/update API
      for (const update of Object.values(recordUpdates)) {
        const payload = {
          customer_name: update.customerName,
          part_number: update.partNumber,
          month: update.month,
          ...update.fields
        };

        await service.updateSalesBudget(payload);
      }

      toast.success("All budget changes saved successfully!");
      setBudgetHasUnsavedChanges(false);
      setBudgetPendingChanges({});
      fetchBudgetData(); // Refresh data
    } catch (error) {
      console.error("Error saving budget changes:", error);
      toast.error(error.response?.data?.message || "Error saving budget changes");
    }
  };

  const handleBudgetCancelAll = () => {
    setBudgetPendingChanges({});
    setBudgetHasUnsavedChanges(false);
    fetchBudgetData(); // Reload original data
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-[#1D1D1D]">Sales Total</h2>
        <button 
          className="bg-[#287DD1] text-white px-6 py-3 rounded-md flex items-center gap-x-2 cursor-pointer"
          onClick={() => navigate('/upload-sales-total')}
        >
          <FaUpload className="text-white" />
          <span className="text-white">Import</span>
        </button>
      </div>

      <Tabs defaultActiveKey="1">
        <TabPane tab="Forecast" key="1">
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center gap-6 mb-4">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Year:</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="border border-gray-300 rounded px-3 py-2"
                  style={{ width: '120px' }}
                >
                  {[2023, 2024, 2025, 2026].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Quarter:</label>
                <Select
                  mode="multiple"
                  placeholder="Select Quarter(s)"
                  value={selectedQuarters}
                  onChange={(values) => setSelectedQuarters(values)}
                  style={{ minWidth: 280 }}
                  size="large"
                  options={[
                    { label: 'Q1 (Apr-Jun)', value: 'Q1' },
                    { label: 'Q2 (Jul-Sep)', value: 'Q2' },
                    { label: 'Q3 (Oct-Dec)', value: 'Q3' },
                    { label: 'Q4 (Jan-Mar)', value: 'Q4' },
                  ]}
                />
              </div>

              <div>
                <button
                  onClick={fetchSalesTotalData}
                  disabled={loading}
                  className="bg-[#3D3D3D] text-white px-6 py-2 rounded-md hover:bg-[#2D2D2D] disabled:bg-gray-400 cursor-pointer"
                  style={{ height: '40px' }}
                >
                  <span className="text-white">{loading ? 'Loading...' : 'Search'}</span>
                </button>
              </div>

              {/* ✅ Forecast Save Buttons */}
              {hasUnsavedChanges && (
                <>
                  <button 
                    onClick={handleSaveAll}
                    className="bg-[#107C41] text-white px-6 py-2 rounded-md hover:bg-[#0e6b37] cursor-pointer"
                    style={{ height: '40px' }}
                  >
                    <span className="text-white">Save Changes</span>
                  </button>
                  <button 
                    onClick={handleCancelAll}
                    className="bg-[#287DD1] text-white px-6 py-2 rounded-md hover:bg-[#2070b8] cursor-pointer"
                    style={{ height: '40px' }}
                  >
                    <span className="text-white">Cancel</span>
                  </button>
                </>
              )}
            </div>
          </div>

          {renderDataTable(data)}
        </TabPane>

        <TabPane tab="Budget" key="2">
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center gap-6 mb-4">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Year:</label>
                <select
                  value={budgetYear}
                  onChange={(e) => setBudgetYear(Number(e.target.value))}
                  className="border border-gray-300 rounded px-3 py-2"
                  style={{ width: '120px' }}
                >
                  {[2023, 2024, 2025, 2026].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Quarter:</label>
                <Select
                  mode="multiple"
                  placeholder="Select Quarter(s)"
                  value={budgetQuarters}
                  onChange={(values) => setBudgetQuarters(values)}
                  style={{ minWidth: 280 }}
                  size="large"
                  options={[
                    { label: 'Q1 (Apr-Jun)', value: 'Q1' },
                    { label: 'Q2 (Jul-Sep)', value: 'Q2' },
                    { label: 'Q3 (Oct-Dec)', value: 'Q3' },
                    { label: 'Q4 (Jan-Mar)', value: 'Q4' },
                  ]}
                />
              </div>

              <div>
                <button
                  onClick={fetchBudgetData}
                  disabled={budgetLoading}
                  className="bg-[#3D3D3D] text-white px-6 py-2 rounded-md hover:bg-[#2D2D2D] disabled:bg-gray-400 cursor-pointer"
                  style={{ height: '40px' }}
                >
                  <span className="text-white">{budgetLoading ? 'Loading...' : 'Search'}</span>
                </button>
              </div>
              
              {/* ✅ ADD SAVE BUTTONS HERE */}
              {budgetHasUnsavedChanges && (
                <>
                  <button 
                    onClick={handleBudgetSaveAll}
                    className="bg-[#107C41] text-white px-6 py-2 rounded-md hover:bg-[#0e6b37] cursor-pointer"
                    style={{ height: '40px' }}
                  >
                    <span className="text-white">Save Changes</span>
                  </button>
                  <button 
                    onClick={handleBudgetCancelAll}
                    className="bg-[#287DD1] text-white px-6 py-2 rounded-md hover:bg-[#2070b8] cursor-pointer"
                    style={{ height: '40px' }}
                  >
                    <span className="text-white">Cancel</span>
                  </button>
                </>
              )}
            </div>
          </div>

          {renderBudgetTable(budgetData)}
        </TabPane>
      </Tabs>
    </div>
  );
};

export default SalesTotal;












